import { useState } from 'react';
import { Spinner } from '../../components/Spinner';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../lib/api';

export function SettingsPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { logout } = useAuth();

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }

    setIsLoading(true);
    try {
      await apiFetch('/v1/auth/change-password', {
        method: 'POST',
        body: {
          currentPassword,
          newPassword,
        },
      });
      setMessage({ type: 'success', text: 'Password changed successfully! Please log in again.' });
      setTimeout(() => logout(), 3000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to change password.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8">
      <h1 className="text-2xl font-bold text-ink mb-8">Account Settings</h1>

      <div className="rounded-2xl border border-line bg-card p-6 sm:p-8 shadow-card">
        <h2 className="text-lg font-semibold text-ink mb-6">Change Password</h2>

        {message && (
          <div
            className={`p-4 rounded-xl mb-6 border ${
              message.type === 'success'
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                : 'bg-err/10 border-err/20 text-err'
            }`}
            role="alert"
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label
              htmlFor="current-password"
              className="block text-xs font-semibold uppercase tracking-wider text-sub mb-1"
            >
              Current Password
            </label>
            <input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full px-3 py-2 rounded-xl border border-line bg-surface text-sm text-ink placeholder:text-muted focus:border-blue focus:ring-1 focus:ring-blue outline-none"
            />
          </div>

          <div>
            <label
              htmlFor="new-password"
              className="block text-xs font-semibold uppercase tracking-wider text-sub mb-1"
            >
              New Password
            </label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 rounded-xl border border-line bg-surface text-sm text-ink placeholder:text-muted focus:border-blue focus:ring-1 focus:ring-blue outline-none"
            />
          </div>

          <div>
            <label
              htmlFor="confirm-password"
              className="block text-xs font-semibold uppercase tracking-wider text-sub mb-1"
            >
              Confirm New Password
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 rounded-xl border border-line bg-surface text-sm text-ink placeholder:text-muted focus:border-blue focus:ring-1 focus:ring-blue outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn-p justify-center py-3 px-4 text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Spinner size="sm" /> Changing...
              </>
            ) : (
              'Change Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
